import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
// CookieConsent is deferred — it's not visible for the first ~1 second anyway,
// and pulling it out of the initial bundle reduces hydration long-tasks (FID).
const CookieConsent = lazy(() => import("./components/CookieConsent"));

// Lazy-load every non-home route. Keeps the initial bundle small and only
// downloads the heavy admin/tool pages when a user actually visits them.
const CategoriesPage = lazy(() => import("./pages/CategoriesPage"));
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const ArticlePage = lazy(() => import("./pages/ArticlePage"));
const HubPage = lazy(() => import("./pages/HubPage"));
const SearchPage = lazy(() => import("./pages/SearchPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const AuthorPage = lazy(() => import("./pages/AuthorPage"));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const GlossaryTermPage = lazy(() => import("./pages/GlossaryTermPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const EditorialPolicyPage = lazy(() => import("./pages/EditorialPolicyPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminArticleStatus = lazy(() => import("./pages/AdminArticleStatus"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const BudgetCalculatorPage = lazy(() => import("./pages/BudgetCalculatorPage"));
const EmergencyFundCalculatorPage = lazy(() => import("./pages/EmergencyFundCalculatorPage"));
const RentAffordabilityCalculatorPage = lazy(() => import("./pages/RentAffordabilityCalculatorPage"));
const MovingCostChecklistPage = lazy(() => import("./pages/MovingCostChecklistPage"));
const MealPlannerPage = lazy(() => import("./pages/MealPlannerPage"));
const FirstPaycheckSimulatorPage = lazy(() => import("./pages/FirstPaycheckSimulatorPage"));
const BillSplitterPage = lazy(() => import("./pages/BillSplitterPage"));
const CleaningScheduleGeneratorPage = lazy(() => import("./pages/CleaningScheduleGeneratorPage"));
const PantryInventoryTrackerPage = lazy(() => import("./pages/PantryInventoryTrackerPage"));
const FirstApartmentChecklistPage = lazy(() => import("./pages/FirstApartmentChecklistPage"));

const queryClient = new QueryClient();

// Minimal, design-system fallback to avoid a flash of unstyled content
// while a route chunk is being fetched.
const RouteFallback = () => <div className="min-h-screen bg-background" aria-hidden="true" />;

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/categories" element={<CategoriesPage />} />
              <Route path="/category/:slug" element={<CategoryPage />} />
              <Route path="/article/:slug" element={<ArticlePage />} />
              <Route path="/hub/:slug" element={<HubPage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/author/:slug" element={<AuthorPage />} />
              <Route path="/glossary" element={<GlossaryPage />} />
              <Route path="/glossary/:slug" element={<GlossaryTermPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/editorial-policy" element={<EditorialPolicyPage />} />
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/tools/budget-calculator" element={<BudgetCalculatorPage />} />
              <Route path="/tools/emergency-fund-calculator" element={<EmergencyFundCalculatorPage />} />
              <Route path="/tools/rent-affordability-calculator" element={<RentAffordabilityCalculatorPage />} />
              <Route path="/tools/moving-cost-checklist" element={<MovingCostChecklistPage />} />
              <Route path="/tools/meal-planner" element={<MealPlannerPage />} />
              <Route path="/tools/first-paycheck-simulator" element={<FirstPaycheckSimulatorPage />} />
              <Route path="/tools/bill-splitter" element={<BillSplitterPage />} />
              <Route path="/tools/cleaning-schedule-generator" element={<CleaningScheduleGeneratorPage />} />
              <Route path="/tools/pantry-inventory-tracker" element={<PantryInventoryTrackerPage />} />
              <Route path="/tools/first-apartment-checklist" element={<FirstApartmentChecklistPage />} />
              <Route path="/admin/article-status" element={<AdminArticleStatus />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Suspense fallback={null}>
            <CookieConsent />
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
